import { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { formatDateTime } from '../../utils/formatDate';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const CONTENT_KEYS: Record<string, string> = {
  terms: 'termsAndConditions',
  license: 'license',
};

const TABS = ['terms', 'license'] as const;
type Tab = (typeof TABS)[number];

const TermsLicenseSection = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<Tab>('terms');
  const [contents, setContents] = useState({ terms: '', license: '' });
  const [originals, setOriginals] = useState({ terms: '', license: '' });
  const [updatedAts, setUpdatedAts] = useState<Record<Tab, string | null>>({
    terms: null,
    license: null,
  });
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState<string | null>(null);

  const isDirty =
    contents.terms !== originals.terms ||
    contents.license !== originals.license;

  const loadContent = useCallback(async (tab: Tab) => {
    const apiKey = CONTENT_KEYS[tab];

    try {
      const data = await api.get<{ value: string; updatedAt: string | null }>(
        `/backoffice/content/${apiKey}`,
      );
      setContents((prev) => ({ ...prev, [tab]: data.value }));
      setOriginals((prev) => ({ ...prev, [tab]: data.value }));
      setUpdatedAts((prev) => ({ ...prev, [tab]: data.updatedAt }));
    } catch {
      setContents((prev) => ({ ...prev, [tab]: '' }));
      setOriginals((prev) => ({ ...prev, [tab]: '' }));
      setUpdatedAts((prev) => ({ ...prev, [tab]: null }));
    }
  }, []);

  useEffect(() => {
    loadContent('terms');
    loadContent('license');
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    setNotif(null);

    try {
      const saves = TABS.filter((tab) => contents[tab] !== originals[tab]).map(
        (tab) =>
          api.put(`/backoffice/content/${CONTENT_KEYS[tab]}`, {
            value: contents[tab],
          }),
      );

      await Promise.all(saves);
      await Promise.all(TABS.map(loadContent));
      setNotif(t('common.save_success'));
      setTimeout(() => setNotif(null), 3000);
    } catch (err: any) {
      setNotif(err.message || t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    loadContent('terms');
    loadContent('license');
  };

  const handleChange = (value: string) => {
    setContents((prev) => ({ ...prev, [activeTab]: value }));
  };

  return (
    <Card>
      <CardContent className="pt-6">
        {/* Tabs */}
        <div className="flex gap-6 mb-4 border-b border-border">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`pb-2 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'text-foreground border-b-2 border-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => {
                setActiveTab(tab);
                setNotif(null);
              }}
            >
              {tab === 'terms'
                ? t('config.terms_title')
                : t('config.license_title')}
            </button>
          ))}
        </div>

        {/* Last updated */}
        {updatedAts[activeTab] && (
          <p className="text-xs text-gray-400 mb-3">
            {t('config.last_updated', {
              date: formatDateTime(updatedAts[activeTab]!),
            })}
          </p>
        )}

        {/* Notification */}
        {notif && (
          <div
            className={`mb-4 px-4 py-2.5 rounded-lg text-sm ${
              notif === t('common.save_success')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-600'
            }`}
          >
            {notif}
          </div>
        )}

        {/* Editor */}
        <div className="border border-border rounded-lg overflow-hidden [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-border [&_.ql-toolbar]:bg-white [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[300px] [&_.ql-editor]:bg-[#F9FAFB]">
          <ReactQuill
            value={contents[activeTab]}
            onChange={handleChange}
            theme="snow"
            placeholder={
              activeTab === 'terms'
                ? t('config.terms_placeholder')
                : t('config.license_placeholder')
            }
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={handleCancel} disabled={!isDirty}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            {saving ? t('common.saving') : t('common.save_changes')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TermsLicenseSection;
